import { useMemo } from 'react';
import Decimal from 'decimal.js';
import {
	DaysPerSimulationPeriod,
	SimulateMicrocreditInput
} from '@/graphql/__generated__/credit-requests';

Decimal.config({
	rounding: Decimal.ROUND_HALF_UP
});

class Money {
	currency: string;
	amount: Decimal;

	constructor(currency: string, amount: string | number | Decimal) {
		this.currency = currency;
		this.amount = new Decimal(amount);
	}

	toString(): string {
		return `${this.amount.toFixed(2)} ${this.currency}`;
	}

	toFormattedString(): string {
		return `${this.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${this.currency}`;
	}
}

interface ParsedMoney {
	amount: Decimal;
	currency: string;
}

function parseMoneyString(moneyStr: string): ParsedMoney {
	if (!moneyStr) return { amount: new Decimal(0), currency: 'MXN' };

	const currencyMatch = moneyStr.match(/[A-Z]{3}/);
	const currency = currencyMatch ? currencyMatch[0] : 'MXN';

	const numericStr = moneyStr.replace(/[^0-9.-]/g, '');
	const amount = new Decimal(numericStr || 0);

	return { amount, currency };
}

function getMaximumNumberOfPeriods(daysPerPeriod: DaysPerSimulationPeriod): number {
	switch (daysPerPeriod) {
		case DaysPerSimulationPeriod.WEEKLY:
			return 240;
		case DaysPerSimulationPeriod.BIWEEKLY:
			return 120;
		case DaysPerSimulationPeriod.MONTHLY:
			return 60;
		default:
			return 60;
	}
}

function getDaysFromPeriod(daysPerPeriod: DaysPerSimulationPeriod): number {
	switch (daysPerPeriod) {
		case DaysPerSimulationPeriod.WEEKLY:
			return 7;
		case DaysPerSimulationPeriod.BIWEEKLY:
			return 15;
		case DaysPerSimulationPeriod.MONTHLY:
			return 30;
		default:
			return 30;
	}
}

function getPaymentPeriodOrdinaryInterestRate(
	annualRate: number | string,
	daysPerPeriod: DaysPerSimulationPeriod
): Decimal {
	const ordinaryInterestRate = new Decimal(annualRate);
	const daysPerPeriodNumber = getDaysFromPeriod(daysPerPeriod);
	const periodRate = new Decimal(360).div(daysPerPeriodNumber).round();
	const result = ordinaryInterestRate.div(periodRate);
	return result;
}

function getPaymentPeriodOrdinaryInterestRateAsDecimal(
	annualRate: number | string,
	daysPerPeriod: DaysPerSimulationPeriod
): Decimal {
	const percentageRate = getPaymentPeriodOrdinaryInterestRate(annualRate, daysPerPeriod);
	return percentageRate.div(100);
}

function decimalPow(base: Decimal, exponent: number): Decimal {
	if (exponent === 0) return new Decimal(1);
	if (exponent === 1) return base;

	let result = base;
	for (let i = 2; i <= exponent; i++) {
		result = result.mul(base);
	}
	return result;
}

function calculateCompoundInterestFactor(interestRate: Decimal, numberOfPeriods: number): Decimal {
	const onePlusRate = new Decimal(1).plus(interestRate);
	const compoundTerm = decimalPow(onePlusRate, numberOfPeriods);
	const reciprocal = new Decimal(1).div(compoundTerm);
	const result = new Decimal(1).minus(reciprocal);
	return result;
}

function calculateHighestAmountToApprove(
	paymentCapacity: string,
	interestRate: number | string,
	daysPerPeriod: DaysPerSimulationPeriod,
	numberOfPeriods: number
): Money {
	const { amount: paymentAmount, currency } = parseMoneyString(paymentCapacity);
	const periodRateDecimal = getPaymentPeriodOrdinaryInterestRateAsDecimal(
		interestRate,
		daysPerPeriod
	);
	const compoundFactor = calculateCompoundInterestFactor(periodRateDecimal, numberOfPeriods);
	const step1 = paymentAmount.mul(compoundFactor);
	const step2 = step1.div(periodRateDecimal);
	const amount = step2.toDecimalPlaces(2);
	return new Money(currency, amount);
}

function calculateMinimumPaymentToSettleCreditTotal(
	amountRequested: string,
	interestRate: number | string,
	daysPerPeriod: DaysPerSimulationPeriod,
	numberOfPeriods: number
): Money {
	const { amount: requestedAmount, currency } = parseMoneyString(amountRequested);
	const periodRateDecimal = getPaymentPeriodOrdinaryInterestRateAsDecimal(
		interestRate,
		daysPerPeriod
	);
	const compoundFactor = calculateCompoundInterestFactor(periodRateDecimal, numberOfPeriods);
	const step1 = requestedAmount.mul(periodRateDecimal);
	const step2 = step1.div(compoundFactor);
	const amount = step2.toDecimalPlaces(2);
	return new Money(currency, amount);
}

function calculateMinimumPaymentToSettleCreditPercentage(
	amountRequested: string,
	interestRate: number | string,
	daysPerPeriod: DaysPerSimulationPeriod,
	numberOfPeriods: number,
	percentageToSettle: number | string
): Money {
	const { amount: requestedAmount, currency } = parseMoneyString(amountRequested);
	const periodRateDecimal = getPaymentPeriodOrdinaryInterestRateAsDecimal(
		interestRate,
		daysPerPeriod
	);
	const percentageDecimal = new Decimal(percentageToSettle).div(100);
	const onePlusRate = new Decimal(1).plus(periodRateDecimal);
	const compoundFactor = decimalPow(onePlusRate, numberOfPeriods);
	const numeratorPart1 = requestedAmount.mul(compoundFactor);
	const numeratorPart2 = requestedAmount.mul(percentageDecimal);
	const numeratorBase = numeratorPart1.minus(numeratorPart2);
	const numerator = numeratorBase.mul(periodRateDecimal);
	const denominator = compoundFactor.minus(1);
	const result = numerator.div(denominator);
	const amount = result.toDecimalPlaces(2);
	return new Money(currency, amount);
}

interface SimulationTermsResult {
	highestAmountToApprove: string | null;
	minimumPaymentToSettleTheCreditTotal: string | null;
	minimumPaymentToSettleTheCreditPercentage: string | null;
	isValid: boolean;
	hasCalculatedValues: boolean;
}

export const useSimulationTerms = (
	formData: SimulateMicrocreditInput,
	serverValues?: {
		highestAmountToApprove?: string;
		minimumPaymentToSettleTheCreditTotal?: string;
		minimumPaymentToSettleTheCreditPercentage?: string;
	}
): SimulationTermsResult => {
	const calculations = useMemo(() => {
		try {
			const paymentCapacity = formData.paymentCapacity || '0.00 MXN';
			const amountRequested = formData.amountRequested || '0.00 MXN';
			const interestRateStr = formData.ordinaryInterestRate || '0';
			const daysPerPeriod = formData.daysPerPeriod || DaysPerSimulationPeriod.MONTHLY;
			const percentageToSettleStr = formData.percentageOfCreditToSettle || '0';

			const interestRate = parseFloat(interestRateStr);
			if (isNaN(interestRate) || interestRate <= 0) {
				return {
					calculatedValues: null,
					isValid: false
				};
			}

			const percentageToSettle = parseFloat(percentageToSettleStr);
			if (isNaN(percentageToSettle)) {
				return {
					calculatedValues: null,
					isValid: false
				};
			}

			const numberOfPeriods = getMaximumNumberOfPeriods(daysPerPeriod);

			const { amount: paymentAmount } = parseMoneyString(paymentCapacity);
			const { amount: requestedAmount } = parseMoneyString(amountRequested);

			if (paymentAmount.lte(0) || requestedAmount.lte(0)) {
				return {
					calculatedValues: null,
					isValid: false
				};
			}

			const highestAmountToApprove = calculateHighestAmountToApprove(
				paymentCapacity,
				interestRate,
				daysPerPeriod,
				numberOfPeriods
			);

			const minimumPaymentToSettleCreditTotal = calculateMinimumPaymentToSettleCreditTotal(
				amountRequested,
				interestRate,
				daysPerPeriod,
				numberOfPeriods
			);

			const minimumPaymentToSettleCreditPercentage =
				calculateMinimumPaymentToSettleCreditPercentage(
					amountRequested,
					interestRate,
					daysPerPeriod,
					numberOfPeriods,
					percentageToSettle
				);

			return {
				calculatedValues: {
					highestAmountToApprove: highestAmountToApprove.toFormattedString(),
					minimumPaymentToSettleTheCreditTotal:
						minimumPaymentToSettleCreditTotal.toFormattedString(),
					minimumPaymentToSettleTheCreditPercentage:
						minimumPaymentToSettleCreditPercentage.toFormattedString()
				},
				isValid: true
			};
		} catch {
			return {
				calculatedValues: null,
				isValid: false
			};
		}
	}, [
		formData.paymentCapacity,
		formData.amountRequested,
		formData.ordinaryInterestRate,
		formData.daysPerPeriod,
		formData.percentageOfCreditToSettle
	]);

	const hasServerValues = Boolean(
		serverValues?.highestAmountToApprove ||
			serverValues?.minimumPaymentToSettleTheCreditTotal ||
			serverValues?.minimumPaymentToSettleTheCreditPercentage
	);

	if (calculations.isValid && calculations.calculatedValues) {
		return {
			highestAmountToApprove: calculations.calculatedValues.highestAmountToApprove,
			minimumPaymentToSettleTheCreditTotal:
				calculations.calculatedValues.minimumPaymentToSettleTheCreditTotal,
			minimumPaymentToSettleTheCreditPercentage:
				calculations.calculatedValues.minimumPaymentToSettleTheCreditPercentage,
			isValid: true,
			hasCalculatedValues: true
		};
	}

	return {
		highestAmountToApprove: serverValues?.highestAmountToApprove || null,
		minimumPaymentToSettleTheCreditTotal:
			serverValues?.minimumPaymentToSettleTheCreditTotal || null,
		minimumPaymentToSettleTheCreditPercentage:
			serverValues?.minimumPaymentToSettleTheCreditPercentage || null,
		isValid: hasServerValues,
		hasCalculatedValues: false
	};
};
